import { lazy, Suspense, useEffect, useRef } from 'react';
import { SessionProvider, useSession } from '../auth/SessionProvider.js';
import { MatchProvider } from '../match/MatchProvider.js';
import { Button } from '../components/Button.js';
import { ErrorBoundary } from '../components/ErrorBoundary.js';
import { applyUpdate, useUpdateAvailable } from '../pwa/serviceWorker.js';
import { StatusBanner } from '../components/StatusBanner.js';
import { MenuScreen } from '../screens/MenuScreen.js';
import { OpeningScreen } from '../screens/OpeningScreen.js';
import { href, useHashRoute, type Location } from './useHashRoute.js';

// Everything past the first two screens is split into its own chunk.
const SettingsScreen = lazy(() => import('../screens/SettingsScreen.js'));
const PlayScreen = lazy(() => import('../screens/PlayScreen.js'));
const CastGallery = lazy(() => import('../screens/CastGallery.js'));
const TutorialScreen = lazy(() => import('../screens/TutorialScreen.js'));
const PrivacyScreen = lazy(() => import('../screens/PrivacyScreen.js'));
const TermsScreen = lazy(() => import('../screens/TermsScreen.js'));

function Screen({ location }: { location: Location }) {
  switch (location.route) {
    case 'opening':
      return <OpeningScreen />;
    case 'menu':
      return <MenuScreen />;
    case 'settings':
      return <SettingsScreen />;
    case 'play':
      return <PlayScreen />;
    case 'cast':
      return <CastGallery />;
    case 'tutorial':
      return <TutorialScreen />;
    case 'privacy':
      return <PrivacyScreen />;
    case 'terms':
      return <TermsScreen />;
  }
}

/** A guest (ADR 0008), or the username of a registered player (ADR 0009). */
function AccountLink() {
  const { state } = useSession();
  if (state.status !== 'ready') return null;
  const account = state.session.account;
  if (account) {
    return (
      <a className="topbar__account" href={href('settings')}>
        <span className="visually-hidden">Signed in as </span>
        <span className="topbar__username">{account.username}</span>
      </a>
    );
  }
  return (
    <a className="topbar__account" href={href('settings')}>
      {/* Short on phones, full on wider screens; screen readers always hear the full text. */}
      <span className="topbar__account-full">Playing as a guest</span>
      <span className="topbar__account-short" aria-hidden="true">
        Guest
      </span>
      <span className="visually-hidden topbar__account-sr">Playing as a guest</span>
    </a>
  );
}

/** A new version has been downloaded; the player decides when to switch to it. */
function UpdateNotice() {
  if (!useUpdateAvailable()) return null;
  return (
    <div className="page page--notice">
      <StatusBanner
        kind="info"
        title="A new version is ready"
        action={
          <Button variant="primary" size="sm" onClick={applyUpdate}>
            Reload
          </Button>
        }
      >
        Reload when it suits you. If you are in a match, your seat is kept.
      </StatusBanner>
    </div>
  );
}

function SessionNotice() {
  const { state } = useSession();
  if (state.status === 'unreachable') {
    return (
      <div className="page page--notice">
        <StatusBanner kind="offline" title="Can’t reach the server">
          Check your connection. We’ll check again when you come back to this tab.
        </StatusBanner>
      </div>
    );
  }
  return null;
}

function Shell() {
  const location = useHashRoute();
  const mainRef = useRef<HTMLElement>(null);

  // Move focus to the new screen so keyboard and screen-reader users land in context.
  useEffect(() => {
    mainRef.current?.focus();
  }, [location.route]);

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      {location.route !== 'opening' && (
        <header className="topbar">
          <a className="topbar__brand" href={href('menu')}>
            <span aria-hidden="true">🚨</span> State of Emergency
          </a>
          <AccountLink />
        </header>
      )}
      <main id="main" ref={mainRef} tabIndex={-1} className="main">
        <UpdateNotice />
        {location.route !== 'opening' && <SessionNotice />}
        <ErrorBoundary key={location.route}>
          <Suspense
            fallback={
              <div className="page">
                <StatusBanner kind="info" title="Loading…" />
              </div>
            }
          >
            <Screen location={location} />
          </Suspense>
        </ErrorBoundary>
      </main>
    </>
  );
}

export function App() {
  return (
    <SessionProvider>
      <MatchProvider>
        <Shell />
      </MatchProvider>
    </SessionProvider>
  );
}
